import { Component, OnDestroy, OnInit } from '@angular/core';
import { NxCheckboxChangeEvent } from '@aposin/ng-aquila/checkbox';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  merge,
  startWith,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ApiState } from 'src/app/model/api-state.model';
import { AppType } from 'src/app/model/app-type.model';
import { FormsService, MAIN_AGENT_COMMISSION } from 'src/app/services/forms.service';
import { PremiumCalculationService } from 'src/app/services/premium-calculation.service';
import { ProcessService, ProcessType } from 'src/app/services/process.service';
import { getAge } from 'src/app/utils';
import { LEGAL_AGE } from 'src/app/utils/getAge.util';
import { STEPS_CONFIG } from '../../config/step.config';
import { environment } from '../../environments/environment';
import { BlockInfo, KnfLimitCheckPayload } from '../../model/async-validation.model';
import { PathPayload } from '../../model/path.model';
import { PersistStorageAgentCommissionSplits } from '../../model/persist-storage.model';
import { PersistStorageKeys, PersistStorageService } from '../../services/persist-storage.service';
import { AppState } from '../../store';
import { selectInsuredImportData } from '../../store/insured-import/insured-import.selectors';
import * as fromProcess from '../../store/process/process.reducer';
import { selectSendBackOfficeData } from '../../store/process/process.reducer';
import isAppAgentOrTravelAgency from '../../utils/is-agent-or-travel-agency.util';
import { BACKOFFICE_PERMISSION } from '../offer/offer.component';
import { GtmService } from '../../services/gtm.service';

const EMPTY_INSUREDS = 0;
const START_CALCULATION_FROM = 0;
const START_INDEX = 0;
const STEP_2_INDEX = 1;
const MIN_COMMISION = 1;
const MAX_COMMISION = 100;
const MAX_COMMISSION_TWO_AGENTS = 99;
const MAX_COMMISSION_THREE_AGENTS = 98;
const INSURED_BIRTH_YEAR_LAST_INDEX = 4;
const DEBOUNCE_TIME = 500;

@Component({
  selector: 'app-step4',
  templateUrl: './step4.component.html',
  styleUrls: ['./step4.component.scss'],
})
export class Step4Component implements OnInit, OnDestroy {
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  formGroup = this.formsService.policyholder;
  agentCommissionSplits = this.formsService.agentCommissionSplits;
  backofficeSection = this.formsService.backofficeSection;
  insureds$ = merge(
    this.store.select(fromProcess.selectQuotationInsureds),
    this.store.select(fromProcess.selectPremiumCalculationInsureds),
  ).pipe(filter((insureds) => !!insureds));
  packageAddons$ = this.store.select(fromProcess.selectPackageAddons);
  selectInsuredImportData$ = this.store.select(selectInsuredImportData);
  selectedTab$ = this.store.select(fromProcess.selectOfferSelectedPackageIndex);
  customizeAlcoholIncluded$ = this.store.select(fromProcess.selectCustomizeAlcoholIncluded);
  discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  selectedPackage$ = this.store.select(fromProcess.selectProcessPackageName);
  addons$ = this.store.select(fromProcess.selectProcessAddons);
  selectedAgentSection$ = this.store.select(fromProcess.selectAgentSection);
  customizeForTheActive$ = this.store.select(fromProcess.selectCustomizeForTheActiveIncluded);
  packages$ = combineLatest([
    this.store.select(fromProcess.selectOfferQuotationData),
    this.store.select(fromProcess.selectProcessKind),
  ]).pipe(map(([data, processKind]) => (processKind ? data?.result.path[processKind]?.packages || [] : [])));
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();
  isAppTravelAgency = environment.app === AppType.TravelAgency;
  isAppAgent = environment.app === AppType.Agent;
  premiumCalculationState$ = this.store.select(fromProcess.selectPremiumCalculationState);
  apiStates = ApiState;
  isActiveRemoveInsureds = false;
  showPremiumCalculationMessage$ = new BehaviorSubject(false);
  countRemoveInsureds$ = new BehaviorSubject(EMPTY_INSUREDS);
  disableRemoveButton$ = this.countRemoveInsureds$.pipe(
    map((count) => count === EMPTY_INSUREDS || count === this.formGroup.value.insureds?.length),
  );
  isRemoveInsuredsInProgress$ = new BehaviorSubject(false);
  blockInfo$ = new Subject<BlockInfo | undefined>();
  insuredPersonCheckbox = false;

  step2path = STEPS_CONFIG()[STEP_2_INDEX].route;
  birthDateErrorVisible$ = new BehaviorSubject(false);
  birthDateErrorYear$ = new BehaviorSubject('');
  sendBackOfficeData$ = this.store.select(selectSendBackOfficeData);
  sendBackOfficeData!: boolean | null;
  isBackOfficeAgent = false;
  isPostingOnAgentBehalf = false;
  twoAgentsMaxCommision = MAX_COMMISSION_TWO_AGENTS;

  ngDestroy$ = new Subject<void>();

  $insuredBirthYears = combineLatest([
    this.store.select(fromProcess.selectProcessKind).pipe(startWith(false)),
    this.formGroup.controls.insureds.valueChanges.pipe(startWith(false)),
  ]).pipe(
    map(([processKind, _]) => {
      switch (processKind) {
        case ProcessType.TRAVELINPL:
          return this.formsService.travelInPl.value.insuredBirthYear;
        case ProcessType.TRIPABROAD:
          return this.formsService.tripAbroad.value.insuredBirthYear;
        case ProcessType.RESIGNATION:
          return Array(this.formsService.resignation.controls.numberOfInsureds.value).fill(null);
        default:
          throw new Error('Unknown process kind');
      }
    }),
  );

  constructor(
    private store: Store<AppState>,
    public formsService: FormsService,
    private processService: ProcessService,
    private persistStorage: PersistStorageService,
    private premiumCalculationService: PremiumCalculationService,
    private gtmService: GtmService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 3 }));
    this.processService.onPageInit();
    this.prepareAgentSplitsForm();
    this.premiumCalculationService.listenPremiumCalculation();
    this.gtmService.step4Event();
    this.checkBlockList();
    if (this.isAppAgentOrTravelAgency) {
      this.checkKnfLimit();
    }

    this.formGroup.controls.insureds.valueChanges
      .pipe(
        takeUntil(this.ngDestroy$),
        debounceTime(DEBOUNCE_TIME),
        filter((insureds) => insureds.every(() => this.formGroup.controls.insureds.valid)),
        tap(() => this.premiumCalculationService.newPremiumCalculation()),
      )
      .subscribe();

    combineLatest([this.formGroup.valueChanges, this.store.select(fromProcess.selectProcessKind)]).subscribe(([formGroup, processKind]) => {
      const insureds = formGroup.insureds;

      if (insureds?.length === EMPTY_INSUREDS) {
        this.birthDateErrorVisible$.next(false);
        this.birthDateErrorYear$.next('');

        return;
      }

      let insuredBirthYear: (number | null)[] | undefined;
      switch (processKind) {
        case ProcessType.TRAVELINPL:
          insuredBirthYear = this.formsService.travelInPl.value.insuredBirthYear;
          break;
        case ProcessType.TRIPABROAD:
          insuredBirthYear = this.formsService.tripAbroad.value.insuredBirthYear;
          break;
      }

      if (insuredBirthYear && insuredBirthYear[START_INDEX] && insureds) {
        const step2year = insuredBirthYear[START_INDEX].toString();
        const insuredDateOfBirth = insureds[START_INDEX].dateOfBirth;

        if (insuredDateOfBirth) {
          const birthYear = insuredDateOfBirth.substring(START_INDEX, INSURED_BIRTH_YEAR_LAST_INDEX);
          const visible = step2year !== birthYear;

          this.birthDateErrorVisible$.next(visible);
          this.birthDateErrorYear$.next(step2year);
        }
      }
    });

    this.formGroup.valueChanges.pipe(takeUntil(this.ngDestroy$)).subscribe((formGroup) => {
      const insuredsToDelete = (formGroup.insureds?.filter((insured) => insured.toRemove) || []).length;
      this.countRemoveInsureds$.next(insuredsToDelete);
      if (insuredsToDelete) {
        this.isActiveRemoveInsureds = true;
      }
    });
    this.formGroup.updateValueAndValidity({ onlySelf: true });

    combineLatest([this.premiumCalculationState$, this.isRemoveInsuredsInProgress$]).subscribe(
      ([premiumCalculationState, isRemoveInsuredsInProgress]) => {
        if (isRemoveInsuredsInProgress && this.isAppAgentOrTravelAgency && premiumCalculationState === this.apiStates.pending) {
          this.showPremiumCalculationMessage$.next(true);
        }
      },
    );

    this.updatePhoneNumber();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  prepareAgentSplitsForm() {
    this.isBackOfficeAgent = this.persistStorage.getString(PersistStorageKeys.AgentCategory) === BACKOFFICE_PERMISSION;
    this.isPostingOnAgentBehalf =
      !!this.formsService.backOfficeSectionApi.impersonateAgentNumber &&
      !!this.formsService.backofficeSection.controls.postingOnAgentBehalf.value;

    const commissionPercent = (
      this.persistStorage.getJson(PersistStorageKeys.FormsAgentCommissionSplits) as PersistStorageAgentCommissionSplits
    ).splits[START_INDEX].commissionPercent;

    this.sendBackOfficeData$.pipe(first()).subscribe((value) => {
      this.sendBackOfficeData = value;
    });

    this.agentCommissionSplits.controls.splits.controls[START_INDEX].setValue({
      agentNumber:
        (this.sendBackOfficeData && this.backofficeSection.value.impersonateAgentNumber) ||
        this.persistStorage.getString(PersistStorageKeys.AgentNumber),
      agentData:
        (this.sendBackOfficeData && this.backofficeSection.value.agentData) ||
        `${String(this.persistStorage.getString(PersistStorageKeys.GivenName))} ${String(
          this.persistStorage.getString(PersistStorageKeys.FamilyName),
        )}`,
      commissionPercent: commissionPercent || MAIN_AGENT_COMMISSION,
    });

    this.agentCommissionSplits.controls.splits.valueChanges.subscribe((data) => {
      const sumValue = data.reduce((a, b) => a + Number(b?.commissionPercent), START_CALCULATION_FROM);
      const startingValue = 100;

      if (data[START_INDEX].agentNumber === this.agentCommissionSplits.controls.splits.controls[START_INDEX].value.agentNumber) {
        this.agentCommissionSplits.controls.splits.controls[START_INDEX].controls.commissionPercent.setValue(startingValue, {
          emitEvent: false,
        });

        return;
      }
      this.agentCommissionSplits.controls.splits.controls[START_INDEX].controls.commissionPercent.setValue(
        sumValue > MAX_COMMISSION_THREE_AGENTS ? MIN_COMMISION : MAX_COMMISION - sumValue,
        {
          emitEvent: false,
        },
      );
    });
  }

  getMaxCommissionForThreeAgents(index: number) {
    const sumValue = this.agentCommissionSplits.controls.splits.controls.reduce(
      (a, b) => a + Number(b.controls.commissionPercent.value),
      START_CALCULATION_FROM,
    );

    if (
      this.agentCommissionSplits.controls.splits.controls[START_INDEX].controls.commissionPercent.value === MIN_COMMISION &&
      sumValue >= MAX_COMMISION
    ) {
      const splitsWithoutCurrent = this.agentCommissionSplits.controls.splits.controls.filter((_, i) => index !== i);
      const otherFieldsSum = splitsWithoutCurrent.reduce((a, b) => a + Number(b.controls.commissionPercent.value), START_CALCULATION_FROM);

      return MAX_COMMISION - otherFieldsSum;
    }

    return MAX_COMMISSION_THREE_AGENTS;
  }

  getInsuredsAddOnNames(i: number): string[] {
    const names: string[] = [];

    combineLatest([this.insureds$, this.selectedTab$, this.packages$])
      .pipe(first())
      .subscribe(([insureds, selectedTab, packages]) => {
        if (insureds) {
          const coverages = insureds[i]?.coverages.filter((coverage) => coverage.selected);

          coverages?.forEach((coverage) => {
            if (coverage.selected) {
              const currentAddon = packages[selectedTab].addOns.find((addOn) => addOn.codeName === coverage.codeName);

              if (currentAddon) {
                names.push(currentAddon.name);
              }
            }
          });
        }
      });

    return names;
  }

  showUnderLegalAgeMessage(i: number, date: string | null | undefined): boolean {
    let showMessage = false;

    combineLatest([this.insureds$, this.processKind$, this.packageAddons$])
      .pipe(first())
      .subscribe(([insureds, processKind, packageAddons]) => {
        if (insureds) {
          const isAlcoholCoverage = packageAddons.state['AlcoholIncluded'];

          if (isAlcoholCoverage && date) {
            let travelDateFrom: string | null = null;
            switch (processKind) {
              case ProcessType.TRAVELINPL:
                travelDateFrom = this.formsService.travelInPl.controls.travelDates.controls.dateFrom.value;
                break;
              case ProcessType.TRIPABROAD:
                travelDateFrom = this.formsService.tripAbroad.controls.travelDates.controls.dateFrom.value;
                break;
              case ProcessType.RESIGNATION:
                travelDateFrom = this.formsService.resignation.controls.travelDates.controls.dateFrom.value;
                break;
              default:
                throw new Error('Unknown process kind');
            }

            if (travelDateFrom) {
              const age = getAge(date, travelDateFrom);
              showMessage = age < LEGAL_AGE;
            }
          }
        }
      });

    return showMessage;
  }

  getMinDate$(i: number): Observable<string | undefined> {
    return this.processKind$.pipe(
      first(),
      map((kind) => {
        switch (kind) {
          case ProcessType.TRIPABROAD: {
            const insuredBirthYear = this.formsService.tripAbroad.value.insuredBirthYear?.[i];

            if (!insuredBirthYear) {
              return undefined;
            }

            return `${insuredBirthYear}-01-01`;
          }
          case ProcessType.TRAVELINPL: {
            const insuredBirthYear = this.formsService.travelInPl.value.insuredBirthYear?.[i];

            if (!insuredBirthYear) {
              return undefined;
            }

            return `${insuredBirthYear}-01-01`;
          }
        }

        return undefined;
      }),
    );
  }

  getMaxDate$(i: number): Observable<string | undefined> {
    return this.processKind$.pipe(
      first(),
      map((kind) => {
        switch (kind) {
          case ProcessType.TRIPABROAD: {
            const insuredBirthYear = this.formsService.tripAbroad.value.insuredBirthYear?.[i];

            if (!insuredBirthYear) {
              return undefined;
            }

            return `${insuredBirthYear}-12-31`;
          }
          case ProcessType.TRAVELINPL: {
            const insuredBirthYear = this.formsService.travelInPl.value.insuredBirthYear?.[i];

            if (!insuredBirthYear) {
              return undefined;
            }

            return `${insuredBirthYear}-12-31`;
          }
        }

        return undefined;
      }),
    );
  }

  getStartViewDate$(i: number): Observable<'month' | 'year' | 'multi-year'> {
    return combineLatest([this.getMinDate$(i), this.getMinDate$(i)]).pipe(
      map(([min, max]) => {
        if (min && max) {
          return 'year';
        }

        return 'multi-year';
      }),
    );
  }

  getStartAtDate$(i: number): Observable<Date> {
    return combineLatest([this.getMinDate$(i), this.getMinDate$(i)]).pipe(
      map(([min, max]) => {
        if (min && max) {
          return new Date(min);
        }

        return new Date('1983');
      }),
    );
  }

  handleToggleActiveRemoveInsureds(): void {
    this.isActiveRemoveInsureds = !this.isActiveRemoveInsureds;
    this.countRemoveInsureds$.next(EMPTY_INSUREDS);
    if (!this.isActiveRemoveInsureds) {
      this.formsService.clearInsuredsToRemoveParam();
    }
  }

  handleRemoveInsureds(): void {
    this.isRemoveInsuredsInProgress$.next(true);

    const indexArr =
      this.formGroup.value.insureds?.reduce<number[]>((acc, insured, index) => {
        if (insured.toRemove) {
          acc.push(index);
        }

        return acc;
      }, []) || [];

    this.formsService.removeInsureds(indexArr);

    this.customizeAlcoholIncluded$
      .pipe(
        first(),
        takeUntil(this.ngDestroy$),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((customizeAlcoholIncluded) => {
        const newCustomizeAlcoholIncluded = customizeAlcoholIncluded?.filter((_, i) => !indexArr.includes(i)) || [];
        const isAlcoholIncluded = newCustomizeAlcoholIncluded?.some(({ selected }) => selected) || false;

        this.store.dispatch(
          fromProcess.actions.selectPackageAddons({
            addon: 'AlcoholIncluded',
            value: isAlcoholIncluded,
          }),
        );

        this.store.dispatch(fromProcess.actions.setCustomizeAlcoholIncluded({ customizeAlcoholIncluded: newCustomizeAlcoholIncluded }));
      });

    this.customizeForTheActive$
      .pipe(
        first(),
        takeUntil(this.ngDestroy$),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((customizeForTheActive) => {
        const newCustomizeForTheActive = customizeForTheActive?.filter((_, i) => !indexArr.includes(i)) || [];
        const isForTheActiveIncluded = newCustomizeForTheActive?.some(({ selected }) => selected) || false;

        this.store.dispatch(
          fromProcess.actions.selectPackageAddons({
            addon: 'ForTheActive',
            value: isForTheActiveIncluded,
          }),
        );

        this.store.dispatch(
          fromProcess.actions.setCustomizeForTheActiveIncluded({ customizeForTheActiveIncluded: newCustomizeForTheActive }),
        );
      });

    this.premiumCalculationService.newPremiumCalculation();
    this.handleToggleActiveRemoveInsureds();
  }

  closeNotification(): void {
    this.isRemoveInsuredsInProgress$.next(false);
    this.showPremiumCalculationMessage$.next(false);
  }
  handleInsuredPerson(event: NxCheckboxChangeEvent): void {
    this.insuredPersonCheckbox = !this.insuredPersonCheckbox;
    if (event.checked) {
      this.formsService.setInsuredPersonParams();
    }
  }

  updatePhoneNumber() {
    this.processKind$.pipe(first()).subscribe((processKind) => {
      if (!this.formsService.policyholder.controls.phoneNumber.value && !this.isAppAgentOrTravelAgency) {
        switch (processKind) {
          case ProcessType.TRIPABROAD:
            if (this.formsService.tripAbroad.controls.phoneNumber.value) {
              this.formsService.policyholder.controls.phoneNumber.setValue(this.formsService.tripAbroad.controls.phoneNumber.value);
            }
            break;
          case ProcessType.TRAVELINPL:
            if (this.formsService.travelInPl.controls.phoneNumber.value) {
              this.formsService.policyholder.controls.phoneNumber.setValue(this.formsService.travelInPl.controls.phoneNumber.value);
            }
            break;
          case ProcessType.RESIGNATION:
            if (this.formsService.resignation.controls.phoneNumber.value) {
              this.formsService.policyholder.controls.phoneNumber.setValue(this.formsService.resignation.controls.phoneNumber.value);
            }
            break;
        }
      }
    });
  }

  private checkBlockList() {
    this.formsService
      .checkBlocklist()
      .pipe(
        takeUntil(this.ngDestroy$),
        tap((blockListResponse) => {
          if (blockListResponse) {
            this.blockInfo$.next(blockListResponse.result);
          }
        }),
      )
      .subscribe();
  }

  private checkKnfLimit() {
    merge(this.formGroup.statusChanges, this.formGroup.valueChanges)
      .pipe(
        takeUntil(this.ngDestroy$),
        distinctUntilChanged(),
        filter(() => this.formsService.policyholder.valid),
        withLatestFrom(this.processKind$, this.discountCode$, this.selectedPackage$, this.addons$, this.selectedAgentSection$),
        debounceTime(DEBOUNCE_TIME),
        tap(([_, processKind, discountCode, packageCodeName, selectedAddons, selectedAgentSection]) => {
          let path: PathPayload;

          switch (processKind?.toLowerCase()) {
            case ProcessType.TRIPABROAD.toLowerCase():
              path = {
                tripAbroad: this.formsService.tripAbroadApi,
              };
              break;
            case ProcessType.TRAVELINPL.toLowerCase():
              path = {
                travelInPL: this.formsService.travelInPlApi,
              };
              break;
            case ProcessType.RESIGNATION.toLowerCase():
              path = {
                resignation: this.formsService.resignationApi,
              };
              break;
            default:
              throw new Error('Unknown process kind');
          }

          const payload: KnfLimitCheckPayload = {
            insureds: this.formsService.insuredsApi,
            path,
            selectedOffert: {
              packageCodeName,
              addOns: selectedAddons ?? {},
            },
            discountCode: {
              code: discountCode || '',
            },
            ...(!(selectedAgentSection.premiumPercentIncrease === undefined || selectedAgentSection.premiumPercentIncrease === null)
              ? {
                  agentSection: selectedAgentSection,
                }
              : {}),
          };

          this.store.dispatch(fromProcess.actions.knfLimitStart({ payload }));
        }),
      )
      .subscribe();
  }
}
